// The transfer status stack's cards and their timers (#6773, epic #6552). `./transfer-stack.ts`
// decides; this holds what it decides over: the cards (oldest first, at most 24), whether the
// stack is open, and per card ONE step timer, ONE clear timer and the task ids it keeps
// followed. Made by `components/TransferStack.tsx`, so it lives exactly as long as the stack is
// mounted, which is only for a person who may move work (`canAdminWrite`).
//
// DERIVED, NEVER PATCHED PER EVENT. A card's state is `transferState` of its own accepted ref
// and its tasks' newest reads (`AcceptedMoves.readOf`, unjudged), re-read whenever a read lands
// or its step timer fires. The one thing written is the LATCH: once a card settles (complete,
// failed, held or retired) its state is frozen, it reads nothing more, and its task ids leave
// the follow's `keep`. A retired card (a later batch moved one of its tasks) leaves at once.
//
// BOUNDED LIFETIMES (a long-lived page). Each card's timers and its hold on the follow belong to
// that card's `mapArray` entry and are released when it settles, is dismissed or cleared, falls
// off the 24 cap, or the stack unmounts. A card leaving slides out for 340 ms and is then
// dropped; that timer is released the same way. Every timer runs on the injected `timers` and
// every time on the injected `now`, never on `MovePage.clock()`, which ticks once a second and
// only while something registered a deadline: the clock is read only to render a countdown.

import {
  type Accessor,
  batch,
  createEffect,
  createMemo,
  createSignal,
  mapArray,
  on,
  onCleanup,
  untrack,
} from 'solid-js';
import { t } from '../i18n';
import type { AcceptedMoves } from './accepted-move';
import type { AcceptedMove, MovePage, MoveRefused } from './move-page';
import type { AcceptedMoveRef } from './move-read';
import type { TransferSoundKind } from './transfer-sound';
import {
  clearAt,
  heldFreeAt,
  insertCard,
  type TransferCard,
  type TransferState,
  transferName,
  transferSettled,
  transferState,
  transferSummary,
} from './transfer-stack';

/** A timer seam: the browser's `setTimeout`, or a test's fake. */
export type TransferTimers = {
  readonly set: (fn: () => void, ms: number) => unknown;
  readonly clear: (handle: unknown) => void;
};

export type TransferStackDeps = {
  readonly page: () => Pick<MovePage, 'boxOf' | 'announce' | 'tickUntil'>;
  readonly accepted: () => Pick<AcceptedMoves, 'readOf' | 'note' | 'newestBatchId' | 'hold'>;
  /** `Date.now` in the browser. */
  readonly now: () => number;
  readonly timers: TransferTimers;
  /** Reduced motion: the step minimums drop to 120 / 300 / 520 ms and nothing slides. */
  readonly reduced: () => boolean;
  readonly sound: (kind: TransferSoundKind) => void;
};

/** How long a card leaving slides out before it is dropped. */
export const LEAVE_MS = 340;

/** One card as the stack shows it. */
export type TransferEntry = {
  readonly card: TransferCard;
  readonly state: Accessor<TransferState>;
  /** The card's Undo window is open: the note names its batch (resting, starting or live). */
  readonly undoOpen: Accessor<boolean>;
};

/** A card sliding out: its last state, gone after 340 ms. */
export type LeavingCard = { readonly card: TransferCard; readonly state: TransferState };

const UNDO_OPEN: ReadonlySet<string> = new Set(['resting', 'starting', 'live']);

/** The batch whose Undo a card can hold: an accepted move's, never a put-back's. */
const batchOf = (card: TransferCard): string | null =>
  card.origin.kind === 'accepted' && !card.putBack ? card.origin.ref.batchId : null;

/** What a card's clear reads of the whole stack, and how it leaves. */
type StackClock = {
  readonly open: Accessor<boolean>;
  readonly releasedAt: Accessor<number | null>;
  readonly remove: (id: number) => void;
};

/** One card's derived state, its latch and its two timers, released with the entry. */
function cardEntry(
  deps: TransferStackDeps,
  stack: StackClock,
  card: TransferCard,
  index: Accessor<number>,
): TransferEntry {
  const [tick, setTick] = createSignal(0);
  const [latch, setLatch] = createSignal<{ state: TransferState; at: number } | null>(null);
  const [undoEndedAt, setUndoEndedAt] = createSignal<number | null>(null);
  let stepTimer: unknown = null;
  let clearTimer: unknown = null;
  const stopStep = (): void => {
    if (stepTimer !== null) deps.timers.clear(stepTimer);
    stepTimer = null;
  };
  const stopClear = (): void => {
    if (clearTimer !== null) deps.timers.clear(clearTimer);
    clearTimer = null;
  };
  const o = card.origin;
  let release: (() => void) | null =
    o.kind === 'accepted' ? untrack(() => deps.accepted().hold(o.taskIds)) : null;
  const letGo = (): void => {
    release?.();
    release = null;
  };
  const live = createMemo(() => {
    tick();
    if (latch() !== null) return null;
    const acc = deps.accepted();
    return transferState(card, (id) => acc.readOf(id), deps.now(), deps.reduced());
  });
  const state = (): TransferState => latch()?.state ?? live()?.state ?? { kind: 'moving', step: 0 };
  createEffect(() => {
    const l = live();
    if (l === null) return;
    stopStep();
    if (transferSettled(l.state)) {
      setLatch({ state: l.state, at: deps.now() });
      letGo();
      // A retired card says nothing; it leaves on its clear timer, at once (`clearAt`).
      if (l.state.kind !== 'retired') deps.sound(soundOf(l.state));
      return;
    }
    if (l.nextAt !== null) {
      stepTimer = deps.timers.set(() => setTick((n) => n + 1), Math.max(0, l.nextAt - deps.now()));
    }
  });
  const undoOpen = createMemo((): boolean => {
    const batch = batchOf(card);
    if (batch === null || state().kind !== 'complete') return false;
    const acc = deps.accepted();
    const n = acc.note();
    return acc.newestBatchId() === batch && n !== null && UNDO_OPEN.has(n.undo.kind);
  });
  createEffect(
    on(undoOpen, (isOpen, wasOpen) => {
      if (wasOpen === true && !isOpen) setUndoEndedAt(deps.now());
    }),
  );
  // A held card's countdown ticks with the page until its wait ends.
  const s0 = untrack(state);
  if (s0.kind === 'held') deps.page().tickUntil(heldFreeAt(s0.refusal));
  createEffect(() => {
    const l = latch();
    stopClear();
    if (l === null) return;
    const at = clearAt(card, l.state, l.at, {
      open: stack.open(),
      releasedAt: stack.releasedAt(),
      index: index(),
      undoOpen: undoOpen(),
      undoEndedAt: undoEndedAt(),
    });
    if (at === null) return;
    clearTimer = deps.timers.set(() => stack.remove(card.id), Math.max(0, at - deps.now()));
  });
  onCleanup(() => {
    stopStep();
    stopClear();
    letGo();
  });
  return { card, state, undoOpen };
}

/** The stack's cards: made, followed, latched, timed and released. */
export function createTransferStack(deps: TransferStackDeps) {
  const [cards, setCards] = createSignal<readonly TransferCard[]>([]);
  const [leaving, setLeaving] = createSignal<readonly LeavingCard[]>([]);
  const [pinned, setPinned] = createSignal(false);
  const [hover, setHover] = createSignal(false);
  const [focus, setFocus] = createSignal(false);
  const [releasedAt, setReleasedAt] = createSignal<number | null>(null);
  const leaveTimers = new Set<unknown>();
  let seq = 0;
  let disposed = false;
  // Collapse pressed while the pointer or focus holds the stack open: it stays collapsed until
  // both have left (the prototype's Collapse only unpins, so it never collapsed a focused stack).
  const [held, setHeld] = createSignal(false);
  const open = (): boolean => pinned() || (!held() && (hover() || focus()));
  createEffect(() => {
    if (!hover() && !focus()) setHeld(false);
  });
  // The stack closed: every card already clearing itself is re-timed from now (`clearAt`).
  createEffect(
    on(open, (isOpen, wasOpen) => {
      if (wasOpen === true && !isOpen) setReleasedAt(deps.now());
    }),
  );
  const stack: StackClock = { open, releasedAt, remove: (id) => remove(id) };
  const entries = createMemo(mapArray(cards, (card, index) => cardEntry(deps, stack, card, index)));
  /** Drop a card: it slides out, then is gone. */
  function remove(id: number): void {
    const e = untrack(entries).find((x) => x.card.id === id);
    if (e === undefined) return;
    const last = { card: e.card, state: untrack(e.state) };
    const slides = !deps.reduced() && !disposed;
    // One update: the card leaves the stack and, unless motion is reduced, starts sliding out.
    const rest = untrack(cards).filter((c) => c.id !== id);
    batch(() => {
      setCards(rest);
      if (rest.length === 0) setPinned(false);
      if (slides) setLeaving((l) => [...l, last]);
    });
    if (!slides) return;
    const h = deps.timers.set(() => {
      leaveTimers.delete(h);
      setLeaving((l) => l.filter((x) => x !== last));
    }, LEAVE_MS);
    leaveTimers.add(h);
  }
  const add = (c: Omit<TransferCard, 'id' | 'arrivedAt'>): void => {
    if (disposed) return;
    seq += 1;
    const next = insertCard(untrack(cards), { ...c, id: seq, arrivedAt: deps.now() });
    setCards(next.cards);
  };
  onCleanup(() => {
    disposed = true;
    for (const h of leaveTimers) deps.timers.clear(h);
    leaveTimers.clear();
  });
  const nameOf = (subject: MoveRefused['subject'], what: string): string =>
    subject === undefined ? what : transferName(subject, t);
  return {
    entries,
    leaving,
    /** Pinned by Expand, hovered by a non-touch pointer, or focus within. */
    open,
    pinned,
    summary: createMemo(() =>
      transferSummary(entries().map((e) => ({ fromId: e.card.fromId, state: e.state() }))),
    ),
    /** A 201 this page's send got, with the ref `AcceptedMoves.accept` follows it by. */
    accepted(a: AcceptedMove, ref: AcceptedMoveRef, order: number): void {
      const req = a.request;
      add({
        order,
        name: nameOf(req.subject, req.what),
        fromId: req.sourceId,
        toId: req.targetId,
        putBack: req.undoOf !== undefined || req.putBack === true,
        origin: {
          kind: 'accepted',
          ref,
          taskIds: (a.accepted.batch.items ?? []).map((i) => i.task_id),
        },
      });
    },
    /** A refusal from this page: a send's, or a drag's at its source or on a drop. */
    refused(r: MoveRefused): void {
      add({
        order: r.order,
        name: nameOf(r.subject, r.what),
        fromId: r.sourceId,
        toId: r.targetId,
        putBack: r.putBack,
        origin: { kind: 'refused', refusal: r.refusal },
      });
    },
    dismiss: remove,
    /** Clear: every card leaves, and the page announces it once. */
    clear(): void {
      for (const e of untrack(entries)) remove(e.card.id);
      setPinned(false);
      deps.page().announce(t('fleet.transfer.cleared'));
    },
    /** Expand pins the stack open; Collapse unpins it and holds it closed while hovered or focused. */
    toggle(): void {
      const wasOpen = untrack(open);
      batch(() => {
        setPinned(!wasOpen);
        // Held closed only against what holds it open now; nothing else will clear it.
        setHeld(wasOpen && untrack(() => hover() || focus()));
      });
    },
    pin(): void {
      setPinned(true);
    },
    setHover,
    setFocus,
  };
}

const soundOf = (s: TransferState): TransferSoundKind =>
  s.kind === 'held' ? 'held' : s.kind === 'failed' ? 'failed' : 'complete';

export type TransferStackStore = ReturnType<typeof createTransferStack>;
